import { FC, useCallback, useEffect, useMemo, useRef } from "react";

import { Button, Collapse } from "antd";
import { groupBy, noop } from "lodash";
import _debounce from "lodash/debounce";
import { useAxisGridlineStore } from "stores/axisGridline/useAxisGridlineStore";
import { useAxisNameStore } from "stores/axisName/useAxisNameStore";
import styled from "styled-components/macro";
import { checkKeyValuePairMatch, excludePropertiesByKeys } from "utils/objects";

import { useLocalStorage } from "hooks";

import { IScreenshotSettings, WidgetComponentType } from "models/screenshot";

import { MPC_AXIS_TITLE_KEYS, isGridlineKey } from "./constants";
import { ScreenshotFormProvider } from "./contexts/ScreenshotFormContext";
import {
  SettingsGroups,
  useScreenshotCalculatedDefaults,
  useScreenshotContext,
  useScreenshotDispatch,
  useScreenshotSettingsControls
} from "./hooks";
import type { SettingsControl } from "./hooks";
import {
  getEffectiveAxisNameId,
  getStoredDashboardScreenshotSettingsKey,
  getStoredScreenshotSettingsKey
} from "./utils";

const { Panel } = Collapse;

const defaultActiveKeys = ["Preset", "Quick Actions"];

type IScreenshotSettingsForm = {
  values: Record<string, unknown>;
  setFormValues: React.Dispatch<React.SetStateAction<Partial<IScreenshotSettings>>>;
};

const ScreenshotSettingsForm: FC<IScreenshotSettingsForm> = ({
  values,
  setFormValues
}) => {
  const calculatedDefaults = useScreenshotCalculatedDefaults();
  const controlGroups = useScreenshotSettingsControls();
  const { widget, widgetState } = useScreenshotContext();

  const ws = widgetState as {
    multiProductActive?: boolean;
    request?: { chartSetting?: { product?: string } };
  };
  const effectiveAxisNameId = getEffectiveAxisNameId(
    widget?.widgetId,
    ws?.multiProductActive,
    ws?.request?.chartSetting?.product
  );

  const isDashboardScreenshot =
    widget?.widgetComponentType === WidgetComponentType.Dashboard;

  const screenshotDispatch = useScreenshotDispatch();

  const settingsKey = isDashboardScreenshot
    ? getStoredDashboardScreenshotSettingsKey(widget?.widgetId)
    : getStoredScreenshotSettingsKey(widget?.widgetId);
  const {
    setStoredValue: setStoredScreenshotSettings,
    getStoredValue: getStoredSettings
  } = useLocalStorage(settingsKey, undefined);

  const setGridline = useAxisGridlineStore((s) => s.setGridline);
  const setAxisName = useAxisNameStore((s) => s.setAxisName);

  const handleResetToPreset = () => {
    screenshotDispatch({
      payload: {
        isResettingPreset: true
      }
    });
  };

  // Pending field edits accumulate here between flushes so concurrent edits to
  // different fields never overwrite each other (a single debounced object literal
  // would only retain the last field's value).
  const pendingRef = useRef<Record<string, unknown>>({});

  // Latest-closure ref: the stable debounce below always runs the freshest logic
  // (calculatedDefaults, storage setters, …) without being recreated each render.
  const applyPendingRef = useRef<() => void>(noop);
  applyPendingRef.current = () => {
    const pending = pendingRef.current;
    pendingRef.current = {};
    const entries = Object.entries(pending);
    if (entries.length === 0) return;

    const isResetToDefault = (name: string, value: unknown) =>
      checkKeyValuePairMatch({ [name]: value }, calculatedDefaults);

    // Persist to storage in a single write so multiple fields can't clobber each
    // other (appendToStoredValue reads-then-sets and is not safe to call in a loop).
    // Gridline and axis-title fields go to their own stores; drop keys reset to their
    // calculated default from screenshotSettings; set the rest.
    let nextStored = getStoredSettings() ?? {};
    entries.forEach(([name, value]) => {
      if (isGridlineKey(name)) {
        // Gridline state lives in the axis gridline store, not in screenshotSettings.
        const isDefault = isResetToDefault(name, value);
        setGridline(widget?.widgetId, name, isDefault ? null : (value as boolean));
        return;
      }
      if (MPC_AXIS_TITLE_KEYS.includes(name)) {
        // Axis names live in the axis name store so they persist across sessions
        // and reset automatically when products change — for both MPC and single-product.
        const isDefault = isResetToDefault(name, value);
        setAxisName(effectiveAxisNameId, name, isDefault ? null : (value as string));
        return;
      }
      nextStored = isResetToDefault(name, value)
        ? excludePropertiesByKeys(nextStored, [name])
        : { ...nextStored, [name]: value };
    });
    setStoredScreenshotSettings(nextStored);

    // Apply every edit to the in-session overrides (MP axis titles included so the
    // live preview reflects them); keys reset to their default are removed.
    setFormValues((prev) => {
      let next: Partial<IScreenshotSettings> = { ...prev };
      entries.forEach(([name, value]) => {
        next = isResetToDefault(name, value)
          ? excludePropertiesByKeys(next, [name])
          : { ...next, [name]: value };
      });
      return next;
    });
  };

  // One stable debounce for the component's lifetime — a fresh _debounce() per
  // render would orphan the pending timer on every re-render.
  const debouncedFlush = useMemo(
    () => _debounce(() => applyPendingRef.current(), 400),
    []
  );
  useEffect(() => () => debouncedFlush.cancel(), [debouncedFlush]);

  // Debounced path — for free-text/number inputs the user types into.
  const onFieldChange = useCallback(
    (name: string, value: unknown) => {
      pendingRef.current = { ...pendingRef.current, [name]: value };
      debouncedFlush();
    },
    [debouncedFlush]
  );

  // Immediate path — for discrete controls (Switch, Select) and blur commits;
  // flushes any pending typed edits alongside the committed value.
  const onFieldCommit = useCallback(
    (name: string, value: unknown) => {
      pendingRef.current = { ...pendingRef.current, [name]: value };
      debouncedFlush.cancel();
      applyPendingRef.current();
    },
    [debouncedFlush]
  );

  const contextValue = useMemo(
    () => ({ values, onFieldChange, onFieldCommit }),
    [values, onFieldChange, onFieldCommit]
  );

  return (
    <ScreenshotFormProvider state={contextValue}>
      <div>
        <Collapse defaultActiveKey={defaultActiveKeys}>
          {Object.entries(groupBy(controlGroups, "group")).map(([key, controls]) => (
            <Panel
              /*
                On the Panel, not on a node inside `header`: rc-collapse spreads
                unknown props onto the outer `.ant-collapse-item` (Panel.js),
                which while collapsed is the whole clickable header row. On the
                header's own content it would mark the label text alone —
                too small to be a useful spotlight, and it leaves the rest of
                the row outside the cut-out where the overlay swallows clicks.
              */
              data-testid={`screenshot-settings-panel-${key
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
              header={key}
              key={key}>
              {(controls as SettingsControl[]).map((sc) => sc.control)}
              {key === SettingsGroups.preset && (
                <PresetActionsContainer>
                  <Button
                    type="primary"
                    onClick={handleResetToPreset}
                    data-testid="reset-screenshot-defaults"
                    data-qa="reset-screenshot-defaults">
                    Restore Defaults
                  </Button>
                </PresetActionsContainer>
              )}
            </Panel>
          ))}
        </Collapse>
      </div>
    </ScreenshotFormProvider>
  );
};

export default ScreenshotSettingsForm;

const PresetActionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
